import { styled, Theme, SxProps } from '@mui/material';
import Stack from '@mui/material/Stack';
import { brightpathTheme } from '@youscience/theme';

export const sxStyles: Record<string, SxProps<Theme>> = {
  schoolLink: (theme: Theme) => ({
    color: theme.palette.grey['600'],
    fontSize: theme.typography.body2.fontSize,
  }),
  contentWrap: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '292px',
  },
  areaOfStudyTitle: {
    margin: '0.75rem 0',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
  },
  collegeOffer: {
    paddingTop: '2rem',
  },
  collegeOfferRow: (theme: Theme) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    rowGap: theme.spacing(1),
    columnGap: '6px',
    color: theme.palette.grey['600'],
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
  }),
  actionsWrap: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: `1px solid ${brightpathTheme.palette.divider}`,
    padding: '1rem 0',
  },
  favorite: {
    display: 'flex',
    justifyContent: 'center',
    width: '50%',

    svg: {
      fill: brightpathTheme.palette.secondary.main,
    },
  },
  divider: {
    height: '48px',
    borderRight: `1px solid ${brightpathTheme.palette.divider}`,
  },
  moreWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '50%',
  },
  moreIcon: {
    '& > svg:first-of-type': {
      marginLeft: '0.5rem',
      fill: brightpathTheme.palette.secondary.main,
    },
  },
};

export const StyledMajorCardWrap = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '296px',
  minHeight: '336px',
  background: theme.palette.common.white,
  boxShadow: `0px 3px 14px 2px ${theme.palette.divider}`,
  borderTop: `5px solid ${theme.palette.primary.main}`,
  borderRadius: '0px 0px 12px 12px',
}));

export const StyledMajorPhoto = styled('div')(() => ({
  position: 'relative',
  height: '184px',
  width: '100%',

  '& > img:first-of-type': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

export const StyledAreaIcon = styled('i')<{ imageSrc: string }>(({ imageSrc, theme }) => ({
  display: 'flex',
  scale: '1.3',
  width: '32px',
  height: '32px',
  maskPosition: 'center',
  maskRepeat: 'no-repeat',
  maskImage: `url(${imageSrc})`,
  backgroundColor: theme.palette.primary.main,
}));

export const StyledPersonalRecommendation = styled('div', {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0 0.5rem',
  border: `1px solid ${color}`,
  color,
  borderRadius: '8px',
  marginRight: '0.625rem',

  '& > div:first-of-type': {
    display: 'flex',
  },

  '& > p:first-of-type': {
    marginTop: '0.125rem',
    marginLeft: '0.375rem',
  },
}));

// The offered text uses primary.dark: primary.main on white is below the AA contrast ratio for body2 text
export const StyledUsheCollegesCount = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isOffered',
})<{ isOffered: boolean }>(({ isOffered, theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  color: isOffered ? theme.palette.primary.dark : theme.palette.grey['600'],
  fontWeight: isOffered ? 600 : 400,
}));

export const StyledUsheLocationIcon = styled('span')({
  display: 'inline-flex',
  flexShrink: 0,

  '& > svg': {
    width: '18px',
    height: '18px',
  },

  // location.svg sets fill on each path, so the svg-level fill from StyledTitlesWrap never reaches it
  '& path': {
    fill: 'currentColor',
  },
});

export const StyledOverallFitWrap = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  padding: '0.375rem 1.25rem',
  minHeight: '38px',
  borderBottom: `1px solid ${theme.palette.grey['300']}`,
}));

export const StyledTitlesWrap = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: '174px',
  padding: '1rem 1.25rem',

  svg: {
    fill: theme.palette.primary.main,
  },
}));
